import { TagItem } from "@/components/business/Tag";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, formatThousands, formatTimestampAuto } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { localizeHref } from "@/paraglide/runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
	AvailableIcon,
	DeleteIcon,
	EditIcon,
	EditIcon2,
	GemIcon2,
	PublishIcon,
	UnAvailableIcon,
} from "~/components/icons";
import { AlertDialogPro } from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { PaginationPro } from "~/components/ui/pagination";
import { type Column, TablePro } from "~/components/ui/table-pro";
import { BaseResponse } from "~/lib/request";
import {
	CreationItem,
	CreationListResponse,
	CreationStatus,
	creationList,
	deleteCreation,
	publishCreation,
	unPublishCreation,
} from "./-api";
import { useCreationTotalsQuery } from "./-hooks/useCreationTotals";
export const Route = createFileRoute("/$lang/profile/creation")({
	component: ProfileCreation,
	validateSearch: (search) => ({
		tab: search.tab,
	}),
});

const PAGE_SIZE = 20;

const tabList = [
	{
		key: "draft",
		label: m.profile_creation_my_draft(),
		icon: <EditIcon2 />,
	},
	{
		key: "publish",
		label: m.profile_creation_publish(),
		icon: <PublishIcon />,
	},
];

type TabType = "draft" | "publish";

type UpdateType = "delete" | "publish" | "unpublish";

function ProfileCreation() {
	const nav = Route.useNavigate();
	const search = Route.useSearch();
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<string | null>(null);
	const tab = search.tab || "draft";
	const [deleteLoading, setDeleteLoading] = useState(false);
	const queryClient = useQueryClient();
	const { draftTotal, publishTotal, refreshTotals } = useCreationTotalsQuery();
	const [page, setPage] = useState(1);

	const { data, isLoading } = useQuery<BaseResponse<CreationListResponse>>({
		queryKey: ["creationList", tab, page],
		queryFn: () =>
			creationList({
				page_num: page,
				page_size: PAGE_SIZE,
				status:
					tab === "draft" ? CreationStatus.DRAFT : CreationStatus.PUBLISHED,
			}),
	});

	const records: CreationListResponse["list"] = data?.data?.list ?? [];
	const total: number = data?.data?.total ?? 0;
	const pageCount = Math.ceil(total / PAGE_SIZE);

	const columns: Column<CreationItem>[] = [
		{
			key: "title",
			title: m.profile_creation_title(),
			className: "text-center",
			render: (v, row) =>
				row.status === CreationStatus.PUBLISHED ? (
					<Link
						to={localizeHref(`/story/${row.creation_id}/-`)}
						className="truncate max-w-[98px] block align-middle text-center hover:underline"
					>
						{v ?? "-"}
					</Link>
				) : (
					<span className="truncate max-w-[98px] block align-middle text-center">
						{v ?? "-"}
					</span>
				),
		},
		{
			key: "tags",
			title: m.profile_creation_tags(),
			className: "text-center",
			render: (tags) => {
				return (
					<div className="flex gap-1 flex-wrap justify-center overflow-hidden max-h-13">
						{tags?.map((tag: any) => (
							<TagItem key={tag.tag_id} label={tag.name} active={false} />
						))}
						{tags?.length === 0 && (
							<span className="text-t1 text-sm font-normal">-</span>
						)}
					</div>
				);
			},
		},
		{
			key: "brief_introduction",
			title: m.profile_creation_description(),
			className: "text-center",
			width: 139,
			render: (v) => (
				<div className="line-clamp-2 break-words max-w-[139px] text-center text-wrap">
					{v ?? "-"}
				</div>
			),
		},
		{
			key: "cover_image",
			className: "text-center ",
			title: m.profile_creation_cover(),
			render: (v) => (
				<div className="flex justify-center min-w-[66px]">
					{v ? (
						<img
							src={v}
							alt=""
							className="w-[66px] h-[66px] aspect-square object-cover rounded-[6px]"
						/>
					) : (
						<div className="text-t1 text-sm font-normal">-</div>
					)}
				</div>
			),
		},
		{
			key: "created_time",
			title:
				tab === "publish"
					? m.profile_creation_publish_time()
					: m.profile_creation_save_time(),
			className: "text-center",
			render: (v) => (
				<span className="flex justify-center">{formatTimestampAuto(v)}</span>
			),
		},
		// 只在发布tab显示的列
		...(tab === "publish"
			? [
					{
						key: "view_count",
						title: m.profile_creation_view_count(),
						className: "text-center",
						render: (v: any) => (
							<span className="flex justify-center">
								{formatThousands(v ?? 0)}
							</span>
						),
					},
					{
						key: "paid_user_count",
						title: m.profile_creation_paid_user_count(),
						className: "text-center",
						render: (v: any) => (
							<span className="flex justify-center">
								{formatThousands(v ?? 0)}
							</span>
						),
					},
					{
						key: "gem_consumption_count",
						title: m.profile_creation_diamond_cost(),
						className: "text-center",
						render: (v: any) => (
							<span className="flex justify-center items-center gap-1">
								{formatThousands(v ?? 0)}
								<GemIcon2 />
							</span>
						),
					},
				]
			: []),
		{
			key: "creation_id",
			className: "text-center",
			title: m.common_operation(),
			sticky: true,
			render: (_, row) => (
				<div className="flex gap-2 justify-center">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className="w-6 h-6 rounded-full bg-bg2 hover:[&>svg>path]:fill-w1"
								onClick={() => {
									nav({
										to: localizeHref(`/edit/${row.creation_id}`),
										search,
									});
								}}
							>
								<EditIcon className="[&>path]:fill-t1" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p>{m.common_edit()}</p>
						</TooltipContent>
					</Tooltip>

					{row.status !== CreationStatus.PUBLISHED && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									className="w-6 h-6 rounded-full bg-bg2 hover:[&>svg>path]:stroke-w1"
									onClick={() => {
										setSelected(row.creation_id);
										setOpen(true);
									}}
								>
									<DeleteIcon className="[&>path]:stroke-t1" />
								</Button>
							</TooltipTrigger>
							<TooltipContent side="bottom">
								<p>{m.common_delete()}</p>
							</TooltipContent>
						</Tooltip>
					)}

					{tab === "draft" ||
						(row.status === CreationStatus.UNPUBLISHED && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										className="w-6 h-6 rounded-full bg-bg2 hover:[&>svg>path]:stroke-w1"
										onClick={async () => {
											const res = await publishCreation(row.creation_id);
											if (res.code === 0) {
												toast.success(m.profile_creation_publish_success());
												updateCreationList("publish", row.creation_id);
												refreshTotals();
											}
										}}
									>
										<AvailableIcon className="[&>path]:stroke-t1" />
									</Button>
								</TooltipTrigger>
								<TooltipContent side="bottom">
									<p>{m.profile_creation_publish_1()}</p>
								</TooltipContent>
							</Tooltip>
						))}
					{tab === "publish" && row.status === CreationStatus.PUBLISHED && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									className="w-6 h-6 rounded-full bg-bg2 hover:[&>svg>path]:stroke-w1"
									onClick={async () => {
										const res = await unPublishCreation(row.creation_id);
										if (res.code === 0) {
											toast.success(m.profile_creation_unpublish_success());
											updateCreationList("unpublish", row.creation_id);
											refreshTotals();
										}
									}}
								>
									<UnAvailableIcon className="[&>path]:stroke-t1" />
								</Button>
							</TooltipTrigger>
							<TooltipContent side="bottom">
								<p>{m.profile_creation_unpublish()}</p>
							</TooltipContent>
						</Tooltip>
					)}
				</div>
			),
		},
	];

	const updateCreationList = (type: UpdateType, targetId: string) => {
		queryClient.setQueryData<BaseResponse<CreationListResponse> | undefined>(
			["creationList", tab, page],
			(oldData) => {
				if (!oldData) return oldData;
				return {
					...oldData,
					data: {
						...oldData.data,
						list: oldData.data?.list
							.filter(
								(item) => type !== "delete" || item.creation_id !== targetId,
							)
							.map((item) => {
								if (item.creation_id !== targetId) return item;
								if (type === "publish")
									return {
										...item,
										status: "PUBLISHED" as keyof typeof CreationStatus,
									};
								if (type === "unpublish")
									return {
										...item,
										status: "UNPUBLISHED" as keyof typeof CreationStatus,
									};
								return item;
							})
							.filter((item) => {
								if (tab === "draft")
									return item.status === CreationStatus.DRAFT;
								if (tab === "publish")
									return (
										item.status === CreationStatus.PUBLISHED ||
										item.status === CreationStatus.UNPUBLISHED
									);
								return true;
							}),
					},
				};
			},
		);
	};

	const deleteHandleConfirm = async () => {
		setDeleteLoading(true);
		if (!selected) return;
		const res = await deleteCreation(selected);

		if (res.code === 0) {
			toast.success(m.profile_creation_delete_success());
			updateCreationList("delete", selected);
			refreshTotals();
			setDeleteLoading(false);
			setOpen(false);
		}
	};

	// 切换tab时，更新URL
	const handleTabChange = (newTab: TabType) => {
		setPage(1); // 切换tab时重置为第一页
		nav({ search: { ...search, tab: newTab } });
	};

	return (
		<div className="p-[22px]">
			{/* 顶部Tabs */}
			<div className="flex gap-[6px] mb-6 justify-center">
				{tabList.map((item) => (
					<button
						type="button"
						key={item.key}
						className={`flex justify-center cursor-pointer md:w-[335px] h-10 items-center text-t1 gap-2 md:px-8 px-4 py-2 rounded-full text-sm transition-all ${
							tab === item.key ? "bg-c1" : "bg-bg2"
						}`}
						onClick={() => handleTabChange(item.key as TabType)}
					>
						{item.icon}
						<span>{item.label}</span>
						<span className="ml-2 px-2 py-0.5 text-w1 bg-main1 rounded-full text-xs font-bold">
							{item.key === "draft" ? draftTotal : publishTotal}
						</span>
					</button>
				))}
			</div>
			<TablePro
				className={cn(
					tab === "publish" && records.length > 0 && "md:w-[1100px]",
				)}
				columns={columns}
				data={records}
				loading={isLoading}
			/>
			{pageCount >= 1 && (
				<div className="flex justify-center py-4">
					<PaginationPro
						page={page}
						pageCount={pageCount}
						onPageChange={setPage}
					/>
				</div>
			)}

			<AlertDialogPro
				open={open}
				content={
					<div className="flex flex-col items-center gap-[6px]">
						<div className="mb-2 w-[120px] h-[120px]">
							<img
								className="w-full h-full object-cover"
								src="/assets/images/profile/creation.png"
								alt="creation"
							/>
						</div>
						<div className="text-t1 text-base font-normal">
							{m.profile_creation_delete_confirm_desc({
								name:
									tab === "draft"
										? m.profile_creation_draft()
										: m.profile_creation_name(),
							})}
						</div>
					</div>
				}
				onCancel={() => setOpen(false)}
				onConfirm={deleteHandleConfirm}
				confirmText={
					deleteLoading
						? m.profile_comment_delete_loading()
						: m.common_confirm()
				}
				cancelText={m.common_cancel()}
				loading={deleteLoading}
			/>
		</div>
	);
}
